import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Images, Layers, Sparkles, Music, Film } from 'lucide-react';
import './AssetsPage.css';
import { useAuthContext } from './components/AuthProvider';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import { useOrgAssetsStore, useOrgAssetsData, fetchOrgAssetsIfStale, fetchOrgAssetDetailIfStale } from './stores';
import OrgAssetCreateView from './components/OrgAssetCreateView';

const SEARCH_DEBOUNCE_MS = 300;

function AssetCard({ asset, onClick, onHover }) {
  const subCount = asset.subAssets?.length || 0;
  const mediaType = asset.mediaType || asset.media_type || 'image';
  const videoUrl = asset.videoUrl || asset.video_url || null;
  const audioUrl = asset.audioUrl || asset.audio_url || null;

  const renderThumb = () => {
    if (mediaType === 'video' && videoUrl) {
      return (
        <video
          src={videoUrl}
          muted
          playsInline
          preload="metadata"
          className="asset-card-image"
        />
      );
    }
    if (mediaType === 'audio' && audioUrl) {
      return (
        <div
          className="asset-card-image"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a1a', color: '#888', width: '100%', height: '100%' }}
          aria-label="Audio asset"
        >
          <Music size={36} strokeWidth={1.25} />
        </div>
      );
    }
    if (mediaType === 'image' && asset.imageUrl) {
      return <img src={asset.imageUrl} alt={asset.name} className="asset-card-image" />;
    }
    return (
      <div className="asset-card-image-placeholder">
        {mediaType === 'video' ? <Film size={28} /> : mediaType === 'audio' ? <Music size={28} /> : <Images size={28} />}
      </div>
    );
  };

  return (
    <div className="asset-card" onClick={onClick} role="button" tabIndex={0}
      onMouseEnter={onHover}
      onFocus={onHover}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}>
      <div className="asset-card-image-wrapper">
        {renderThumb()}
        {subCount > 0 && (
          <span className="asset-card-sub-badge">
            <Layers size={10} />
            {subCount}
          </span>
        )}
      </div>
      <div className="asset-card-info">
        <span className="asset-card-name">{asset.name}</span>
        {asset.prompt && (
          <span className="asset-card-prompt">{asset.prompt}</span>
        )}
      </div>
    </div>
  );
}

function AssetsPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuthContext();

  const addOrgAsset = useOrgAssetsStore((state) => state.addOrgAsset);
  const { items: orgAssets, isLoading, error: storeError, hasCache } = useOrgAssetsData(user?.id);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showCreateView, setShowCreateView] = useState(false);

  const searchTimeoutRef = useRef(null);

  // Debounce search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchQuery]);

  // SWR: render cached list immediately, revalidate in background.
  useEffect(() => {
    if (loading || !user) return;
    fetchOrgAssetsIfStale({ userId: user.id });
  }, [user, loading]);

  const filteredAssets = useMemo(() => {
    if (!debouncedSearch.trim()) return orgAssets;
    const q = debouncedSearch.toLowerCase();
    return orgAssets.filter((a) => a.name?.toLowerCase().includes(q) || a.prompt?.toLowerCase().includes(q));
  }, [orgAssets, debouncedSearch]);

  if (showCreateView) {
    return (
      <AuthenticatedLayout>
        <div className="assets-page-create-wrapper">
          <OrgAssetCreateView
            onAssetGenerated={(asset) => {
              addOrgAsset(asset);
              setShowCreateView(false);
              navigate(`/assets/${asset.id}`);
            }}
            onCancel={() => setShowCreateView(false)}
          />
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <div className="assets-page-content">
        {/* Header */}
        <div className="assets-page-header-section">
          <div className="assets-page-header-left">
            <h1 className="assets-page-title">Images</h1>
            <p className="assets-page-subtitle">
              Reusable images shared across all your projects
            </p>
          </div>

          <button
            type="button"
            className="assets-page-new-button"
            onClick={() => setShowCreateView(true)}
          >
            <Sparkles size={14} />
            Create New
          </button>
        </div>

        {/* Search */}
        <div className="assets-page-search-row">
          <input
            type="text"
            className="assets-page-search"
            placeholder="Search images…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Error */}
        {storeError && (
          <div className="assets-page-error">{storeError}</div>
        )}

        {/* Loading (cold first load only) */}
        {isLoading && !hasCache && (
          <div className="assets-page-loading">Loading images…</div>
        )}

        {/* Empty state */}
        {hasCache && !storeError && filteredAssets.length === 0 && (
          <div className="assets-page-empty">
            <Images size={40} className="assets-page-empty-icon" />
            {debouncedSearch ? (
              <p>No images match your search.</p>
            ) : (
              <>
                <p>No images yet.</p>
                <p className="assets-page-empty-hint">
                  Create your first AI image to reuse across all projects.
                </p>
                <button
                  className="assets-page-empty-cta"
                  onClick={() => setShowCreateView(true)}
                >
                  <Sparkles size={14} />
                  Create your first image
                </button>
              </>
            )}
          </div>
        )}

        {/* Grid */}
        {hasCache && filteredAssets.length > 0 && (
          <div className="assets-page-grid">
            {filteredAssets.map((asset) => (
              <AssetCard
                key={asset.id}
                asset={asset}
                onClick={() => navigate(`/assets/${asset.id}`)}
                onHover={() => fetchOrgAssetDetailIfStale({ id: asset.id, userId: user?.id })}
              />
            ))}
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
}

export default AssetsPage;
